import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../services/Api';
import { IoAdd } from 'react-icons/io5';
import Title from '../components/Title';
import { setCurrentPage } from '../store/slice/paginationSlice';
import {
  dismissToast,
  showError,
  showLoading,
  showSuccess,
} from '../components/Ui/ToastNotification';
import Pagination from '../components/Pagination';

const Faq = () => {
  const dispatch = useDispatch();
  const { currentPage } = useSelector(state => state.pagination);
  const searchQuery = useSelector(state => state.search.query);
  const [faqItems, setFaqItems] = useState([]);
  const [newFaq, setNewFaq] = useState({
    question: '',
    answer: '',
    isEditing: false,
    editingId: null,
    isActive: 'true',
  });
  const [category, setCategory] = useState('');

  const entriesPerPage = 5;

  const fetchFaqs = async () => {
    const loadingToast = showLoading('Fetching FAQs...');
    try {
      const response = await api.get('/faq');
      setFaqItems(response.data.faq);
      dismissToast(loadingToast);
      showSuccess('FAQs loaded successfully');
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      dismissToast(loadingToast);
      showError('Failed to load FAQs');
    }
  };

  useEffect(() => {
    fetchFaqs();
    dispatch(setCurrentPage(1));
  }, [dispatch]);

  const filteredFaqItems = faqItems?.filter(item => {
    if (!searchQuery) return true;

    const question = item?.question || '';
    const questionMatch = question.toLowerCase().includes(searchQuery.toLowerCase());

    let answers = [];
    if (Array.isArray(item?.answer)) {
      answers = item.answer;
    } else if (item?.answer) {
      answers = [item.answer];
    }

    const answerMatch = answers.some(answer =>
      String(answer || '')
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );

    return questionMatch || answerMatch;
  });

  const indexOfLastItem = currentPage * entriesPerPage;
  const indexOfFirstItem = indexOfLastItem - entriesPerPage;
  const currentFaqItems = filteredFaqItems.slice(indexOfFirstItem, indexOfLastItem);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setNewFaq(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (newFaq.isEditing) {
      await updateFaq(newFaq.editingId);
    } else {
      await createFaq();
    }
  };

  const createFaq = async () => {
    if (!newFaq.question || !newFaq.answer) {
      showError('Please fill in both question and answer fields');
      return;
    }

    const loadingToast = showLoading('Creating FAQ...');
    try {
      await api.post('/faq', {
        question: newFaq.question,
        answer: [newFaq.answer],
        category,
        isActive: newFaq.isActive,
      });

      await fetchFaqs();
      resetNewFaq();
      dismissToast(loadingToast);
      showSuccess('FAQ created successfully');
    } catch (error) {
      console.error('Error creating FAQ:', error);
      dismissToast(loadingToast);
      showError('Failed to create FAQ');
    }
  };

  const updateFaq = async id => {
    if (!newFaq.question || !newFaq.answer) {
      showError('Please fill in both question and answer fields');
      return;
    }

    const loadingToast = showLoading('Updating FAQ...');
    try {
      const updateData = {
        question: newFaq.question,
        answer: [newFaq.answer],
        category,
        isActive: String(newFaq.isActive),
      };

      const response = await api.patch(`/faq/${id}`, updateData);
      setFaqItems(prevItems => prevItems.map(item => (item._id === id ? response.data.faq : item)));
      resetNewFaq();
      dismissToast(loadingToast);
      showSuccess('FAQ updated successfully');
    } catch (error) {
      console.error('Error updating FAQ:', error);
      dismissToast(loadingToast);
      showError('Failed to update FAQ');
    }
  };

  const deleteFaq = async id => {
    const loadingToast = showLoading('Deleting FAQ...');
    try {
      await api.delete(`/faq/${id}`);
      setFaqItems(prevItems => prevItems.filter(item => item._id !== id));
      dismissToast(loadingToast);
      showSuccess('FAQ deleted successfully');
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      dismissToast(loadingToast);
      showError('Failed to delete FAQ');
    }
  };

  const resetNewFaq = () => {
    setNewFaq({
      question: '',
      answer: '',
      isEditing: false,
      editingId: null,
      isActive: 'true',
    });
    setCategory('');
  };

  const handleEdit = index => {
    const actualIndex = indexOfFirstItem + index;
    const itemToEdit = filteredFaqItems[actualIndex];
    setNewFaq({
      question: itemToEdit.question,
      answer: itemToEdit.answer?.[0] || '',
      isEditing: true,
      editingId: itemToEdit._id,
      isActive: itemToEdit.isActive.toString(),
    });
    setCategory(itemToEdit.category || '');
  };

  const EmptyFaqState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4 text-gray-400">
        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h3 className="mb-2 text-sm font-light text-gray-600">
        {searchQuery ? 'No matching FAQs found' : 'No FAQs yet'}
      </h3>
      <p className="max-w-md mb-6 text-xs font-light text-gray-500">
        {searchQuery
          ? 'No FAQs match your search criteria. Try adjusting your search terms.'
          : 'Get started by creating your first frequently asked question to help your users.'}
      </p>
      {!searchQuery && (
        <p className="text-xs font-light text-gray-500">
          Fill out the form above to create your first FAQ.
        </p>
      )}
    </div>
  );

  return (
    <div className="p-3 sm:ml-64 w-[80%] bg-[#F4F5F6] font-nohemi">
      <div className="px-4">
        <Title title="FAQ" />
        <div className="w-full p-6 mb-4 bg-white rounded-lg">
          <form onSubmit={handleSubmit}>
            <div className="mt-4">
              <label htmlFor="question" className="text-sm text-[#979797] font-semibold">
                Question
              </label>
              <input
                type="text"
                data-testid="question-input"
                name="question"
                value={newFaq.question}
                onChange={handleInputChange}
                className="w-full p-2 border border-[#D5D2D2] rounded-lg outline-none"
              />
            </div>
            <div className="mt-4">
              <label htmlFor="answer" className="text-sm text-[#979797] font-semibold">
                Answer
              </label>
              <textarea
                name="answer"
                data-testid="answer-input"
                value={newFaq.answer}
                onChange={handleInputChange}
                className="w-full p-2 border border-[#D5D2D2] rounded-lg outline-none"
                rows={5}
              />
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center w-3/4">
                <div className="mr-4">
                  <label htmlFor="category" className="text-sm text-[#979797] font-semibold mr-2">
                    Category
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    className="p-2 border border-[#D5D2D2] rounded-lg outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="isActive" className="text-sm text-[#979797] font-semibold mx-4">
                    Is Active
                  </label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={newFaq.isActive === 'true'}
                    onChange={e =>
                      setNewFaq(prev => ({
                        ...prev,
                        isActive: e.target.checked.toString(),
                      }))
                    }
                    className="accent-black"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-semibold text-white bg-black rounded-lg">
                {newFaq.isEditing ? 'Update' : 'Add'} FAQ
              </button>
            </div>
          </form>

          <div className="mt-6">
            {filteredFaqItems.length === 0 ? (
              <EmptyFaqState />
            ) : (
              <>
                {(Array.isArray(currentFaqItems) ? currentFaqItems : []).map((item, index) => (
                  <div
                    key={item._id}
                    className="flex flex-row px-4 py-2 mb-2 bg-white border rounded">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full my-auto bg-[#F0F6FF]">
                      <IoAdd className="text-[#33363F]" />
                    </div>
                    <div className="w-4/5 ml-3 mr-6">
                      <h2 className="text-sm text-[#191B1C] font-semibold mb-2">{item.question}</h2>
                      <p className="text-[#626C70] text-sm">
                        {Array.isArray(item.answer)
                          ? item.answer.join(', ')
                          : 'No answer available'}
                      </p>
                      <p className="text-[#191B1C] text-sm">Category: {item.category}</p>
                    </div>
                    <div className="flex items-center justify-end mr-3 space-x-2 w-1/10">
                      <button
                        onClick={() => handleEdit(index)}
                        className="p-2 text-sm font-light text-black bg-white border border-black rounded-lg">
                        Edit
                      </button>
                      <button
                        onClick={() => deleteFaq(item._id)}
                        className="p-2 text-sm font-light text-white bg-black rounded-lg">
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {filteredFaqItems.length > 0 && <Pagination totalEntries={filteredFaqItems.length} />}
        </div>
      </div>
    </div>
  );
};

export default Faq;
